import { useLockedBody } from "@/app/hooks/locked_body"
import { Calculation } from "@/app/types"
import { isNotNull } from "@/app/utils"
import { CaretDown, Info, X } from "@phosphor-icons/react"
import { useCallback, useEffect, useRef, useState } from "react"

export const PopupComponent = ({
  setOpenGroupKey,
  openGroupKey,
  topLevel,
  topLevelSelectedKey,
  close,
  isAccordian,
  selectedKey,
  isThereOnlyOneItemInOptions,
  header,
  footer,
}) => {
  const [hasInfo, setHasInfo] = useState(false)
  const [infoText, setInfoText] = useState({
    key: "",
    text: "",
    isOpen: false,
  })

  const modalRef = useRef(null)

  const handleInfoIconClick = (e: Event, item) => {
    e.stopPropagation()
    setInfoText((prevState) => ({
      key: prevState.isOpen && prevState.key === item.key ? "" : item.key,
      text: prevState.isOpen && prevState.key === item.key ? "" : item.infoText,
      isOpen: !prevState.isOpen || prevState.key !== item.key,
    }))
  }

  const checkForCalculationLabelsInformation = () => {
    const calculationLabels = Object.keys(Calculation.CALCNAMES)
    if (calculationLabels.includes(selectedKey)) {
      setHasInfo(true)
    }
  }

  const [_, setBodyLock] = useLockedBody(isNotNull(selectedKey))

  // setup a function that will enable scrolling
  const closePopup = useCallback(() => {
    setBodyLock(false)
    close()
  }, [close, setBodyLock])

  useEffect(() => {
    checkForCalculationLabelsInformation()
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        closePopup()
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [close])

  return (
    <div className={`absolute top-0 w-full`} ref={modalRef}>
      <div
        className={`flex max-h-[90vh] w-full overflow-y-auto rounded-b-3xl bg-main-light ${footer ? "pt-4" : "p-4"}`}>
        <div className="w-full">
          <X
            onClick={() => closePopup()}
            strokeWidth={2.5}
            className="absolute right-6 top-4 z-50 h-7 w-7 cursor-pointer opacity-50"
          />
          {header}
          <div className={`${footer ? "px-4" : ""}`}>
            <ul className="w-full divide-y divide-main-200">
              {topLevel.map((item) => {
                // Render only the parent if it has a single child
                if (isThereOnlyOneItemInOptions) {
                  const singleChild = item.items ? item.items[0] : item
                  return (
                    <li
                      key={item.key}
                      className={`py-3 ${singleChild.disabled ? "opacity-50" : "opacity-100"}`}>
                      <div
                        className={`flex cursor-pointer select-none items-center gap-2 ${
                          item.key === topLevelSelectedKey ? "font-bold" : ""
                        } ${singleChild.disabled ? "text-main-400" : "text-main-dark"}`}
                        onClick={() =>
                          !singleChild.disabled &&
                          close(singleChild.key, item.key)
                        }>
                        {item.text}
                      </div>
                    </li>
                  )
                }
                return (
                  <li
                    key={item.key}
                    className={`flex flex-col ${item.disabled ? "opacity-50" : "opacity-100"}`}>
                    <div
                      className={`flex select-none items-center gap-2 py-2 ${
                        item.key == topLevelSelectedKey ? "font-bold" : ""
                      } ${item.disabled ? "text-main-400" : "text-main-dark"}`}>
                      <div
                        className="flex cursor-pointer items-center gap-1"
                        onClick={() =>
                          !item.disabled &&
                          (isAccordian ?
                            setOpenGroupKey(item.key)
                          : close(item.key, null))
                        }>
                        {item.text}
                        {hasInfo && (
                          <div>
                            {!infoText.isOpen || infoText.key !== item.key ?
                              <Info
                                onClick={(e) => {
                                  handleInfoIconClick(e, item)
                                }}
                                className="h-5 w-5 text-main-600"
                                aria-hidden="true"
                              />
                            : <X
                                onClick={(e) => {
                                  handleInfoIconClick(e, item)
                                }}
                                className="h-5 w-5 text-main-600"
                                aria-hidden="true"
                                name="X"
                              />
                            }
                          </div>
                        )}
                        {isAccordian && (
                          <div
                            className={`transform ${
                              item.key == openGroupKey ?
                                "rotate-0"
                              : "-rotate-90"
                            }`}>
                            <CaretDown />
                          </div>
                        )}
                      </div>
                    </div>
                    {hasInfo && infoText.isOpen && infoText.key === item.key ?
                      <div className="pb-1 text-xs sm:text-sm">
                        {infoText.text}
                      </div>
                    : null}
                    {isAccordian && item.key == openGroupKey && (
                      <ul className="pl-4">
                        {item.items.map((childItem) => (
                          <li
                            key={childItem.key}
                            className={`py-1 text-sm ${
                              childItem.disabled ? "text-main-400" : (
                                "cursor-pointer text-main-dark"
                              )
                            } ${
                              (
                                childItem.key == selectedKey &&
                                openGroupKey == topLevelSelectedKey
                              ) ?
                                "font-bold"
                              : ""
                            }`}
                            onClick={() =>
                              !childItem.disabled &&
                              close(childItem.key, openGroupKey)
                            }>
                            {childItem.text}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
          {footer}
        </div>
      </div>
    </div>
  )
}
